/**
 * HostedAgentConnectionTab — Connection tab for hosted GHCP agents.
 *
 * Shows a list of hosted agents from hostedAgentConnections with status badges.
 * "Create new" navigates to the Agents page where the HostedAgentWizard lives.
 */

import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  Text,
  Badge,
  Button,
  Spinner,
  mergeClasses,
} from '@fluentui/react-components';
import {
  CheckmarkCircle16Filled,
  PlugDisconnected16Regular,
  Add16Regular,
  Open16Regular,
  ErrorCircle16Filled,
  Warning16Filled,
} from '@fluentui/react-icons';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';
import { useHostedAgents } from '@/hooks/useHostedAgent';
import type { HostedAgentStatus } from '@/types/hostedAgent';

/** Short status labels for the selected-agent card subtitle. */
const STATUS_LABELS: Record<HostedAgentStatus, string> = {
  connected: 'Connected',
  provisioning: 'Provisioning',
  auth_failed: 'Authentication failed',
  agent_error: 'Agent error',
  version_mismatch: 'Update available',
  unreachable: 'Unreachable',
  unknown: 'Status unknown',
};

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    flex: 1,
    minHeight: 0,
  },

  // Selected agent card with left border accent
  selectedCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderLeftWidth: '3px',
    borderLeftStyle: 'solid',
    borderLeftColor: tokens.colorNeutralStroke2,
  },
  selectedCardConnected: {
    borderLeftColor: tokens.colorPaletteGreenBorder1,
  },

  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
  },
  statusIcon: {
    fontSize: tokens.fontSizeBase400,
    flexShrink: 0,
  },
  statusIconConnected: {
    color: tokens.colorPaletteGreenForeground1,
  },
  statusIconNone: {
    color: tokens.colorNeutralForeground3,
  },
  statusIconError: {
    color: tokens.colorPaletteRedForeground1,
  },
  statusIconWarning: {
    color: tokens.colorPaletteMarigoldForeground1,
  },
  statusInfo: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  statusTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  statusSubtitle: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },

  // List section
  listSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  listHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  listTitle: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
    textTransform: 'uppercase',
  },

  // Agent items
  agentList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    flex: 1,
    overflowY: 'auto',
    listStyleType: 'none',
    margin: 0,
    padding: 0,
  },
  agentItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    border: `1px solid transparent`,
    backgroundColor: 'transparent',
    // Native <button> resets so it matches the previous <div> row layout.
    width: '100%',
    appearance: 'none',
    textAlign: 'left',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    color: 'inherit',
    transitionProperty: 'background-color, border-color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3,
    },
    ':focus-visible': {
      outlineStyle: 'solid',
      outlineWidth: '2px',
      outlineColor: tokens.colorBrandStroke1,
      outlineOffset: '-2px',
    },
  },
  agentItemSelected: {
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorBrandStroke1}`,
  },
  agentItemInfo: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  agentItemName: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  agentItemModel: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  // Create new button
  createNewButton: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    backgroundColor: 'transparent',
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
    // Native <button> resets so it matches the previous <div> row layout.
    width: '100%',
    appearance: 'none',
    textAlign: 'left',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    color: 'inherit',
    transitionProperty: 'background-color, border-color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3,
      border: `1px dashed ${tokens.colorBrandStroke1}`,
    },
    ':focus-visible': {
      outlineStyle: 'solid',
      outlineWidth: '2px',
      outlineColor: tokens.colorBrandStroke1,
      outlineOffset: '-2px',
    },
  },
  createNewIcon: {
    width: tokens.spacingHorizontalXXL,
    height: tokens.spacingHorizontalXXL,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorBrandBackground2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: tokens.colorBrandForeground1,
  },
  createNewText: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
  },

  // Empty state
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: tokens.spacingVerticalL,
    textAlign: 'center',
    gap: tokens.spacingVerticalS,
  },
  emptyIcon: {
    fontSize: tokens.fontSizeBase600,
    color: tokens.colorNeutralForeground4,
  },
  emptyText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },

  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalM,
  },
});

// =============================================================================
// HELPERS
// =============================================================================

function getStatusBadgeColor(status: HostedAgentStatus): 'success' | 'danger' | 'informative' {
  switch (status) {
    case 'connected': return 'success';
    case 'auth_failed':
    case 'agent_error':
    case 'unreachable': return 'danger';
    default: return 'informative';
  }
}

// =============================================================================
// COMPONENT
// =============================================================================

export const HostedAgentConnectionTab: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { agents, isLoading } = useHostedAgents();

  const parameterValues = useNodeEditorStore((s) => s.parameterValues);
  const setParameter = useNodeEditorStore((s) => s.setParameter);

  // Current selected agent name. The durable key is _modelConnection_model1
  // (persisted via modelConfigurations.model1.referenceName); hostedAgentName is
  // deleted on serialize for FoundryHostedAgent, so it's only present during an
  // in-memory session and gone after save+reopen.
  const selectedAgentName = (parameterValues['_modelConnection_model1']
    ?? parameterValues['hostedAgentName']
    ?? parameterValues['parameters.hostedAgentName']
    ?? '') as string;

  const selectedAgent = useMemo(
    () => agents.find((a) => a.key === selectedAgentName),
    [agents, selectedAgentName],
  );

  const handleSelectAgent = useCallback(
    (agentKey: string) => {
      setParameter('hostedAgentName', agentKey);
      // Set the modelConfigurations reference for runtime connection resolution
      setParameter('_modelConnection_model1', agentKey);
    },
    [setParameter],
  );

  const handleCreateNew = useCallback(() => {
    navigate('/agents');
  }, [navigate]);

  // Status info for the selected card
  // Pick a distinct glyph per status so a row is scannable at a glance
  // (checkmark = healthy, spinner = provisioning, error = down/auth-failed,
  // warning = update available) instead of a recolored checkmark that always
  // reads as "OK". Shared by the list rows and the selected-agent card.
  const renderStatusGlyph = (status: HostedAgentStatus): React.ReactNode => {
    switch (status) {
      case 'connected':
        return <CheckmarkCircle16Filled aria-hidden="true" className={mergeClasses(styles.statusIcon, styles.statusIconConnected)} />;
      case 'provisioning':
        return <Spinner size="tiny" aria-hidden className={styles.statusIcon} />;
      case 'auth_failed':
      case 'agent_error':
      case 'unreachable':
        return <ErrorCircle16Filled aria-hidden="true" className={mergeClasses(styles.statusIcon, styles.statusIconError)} />;
      case 'version_mismatch':
        return <Warning16Filled aria-hidden="true" className={mergeClasses(styles.statusIcon, styles.statusIconWarning)} />;
      default:
        return <Warning16Filled aria-hidden="true" className={mergeClasses(styles.statusIcon, styles.statusIconNone)} />;
    }
  };

  const getSelectedStatusInfo = () => {
    if (!selectedAgentName) {
      return {
        status: 'none' as const,
        title: 'No agent selected',
        subtitle: 'Select one below',
        icon: <PlugDisconnected16Regular aria-hidden="true" className={mergeClasses(styles.statusIcon, styles.statusIconNone)} />,
      };
    }

    // Agent is selected — reflect its real status. If the referenced agent isn't in
    // the list (e.g. deleted), surface that as unknown/not-found rather than "connected".
    const agentStatus: HostedAgentStatus = selectedAgent?.status ?? 'unknown';

    return {
      status: agentStatus,
      title: selectedAgent?.displayName ?? selectedAgentName,
      subtitle: selectedAgent ? STATUS_LABELS[agentStatus] : 'Agent not found',
      icon: renderStatusGlyph(agentStatus),
    };
  };

  const statusInfo = getSelectedStatusInfo();

  return (
    <div className={styles.container} data-testid="hosted-agent-connection-tab">
      {/* Current Selection Card */}
      <div
        className={mergeClasses(
          styles.selectedCard,
          statusInfo.status === 'connected' && styles.selectedCardConnected,
        )}
        data-testid="hosted-agent-status-card"
      >
        <div className={styles.cardHeader}>
          {statusInfo.icon}
          <div className={styles.statusInfo}>
            <Text className={styles.statusTitle}>{statusInfo.title}</Text>
            <Text className={styles.statusSubtitle}>{statusInfo.subtitle}</Text>
          </div>
          {selectedAgent && (
            <Button
              appearance="subtle"
              size="small"
              icon={<Open16Regular />}
              onClick={() => navigate(`/agents/${selectedAgent.configuration.agentName}`)}
              aria-label="View agent details"
            />
          )}
        </div>
      </div>

      {/* Agent List */}
      <div className={styles.listSection}>
        <div className={styles.listHeader}>
          <Text className={styles.listTitle}>Hosted Agents</Text>
          {agents.length > 0 && (
            <Badge appearance="tint" color="informative" size="small">
              {agents.length}
            </Badge>
          )}
        </div>

        {/* Create new — navigates to Agents page */}
        <button
          type="button"
          className={styles.createNewButton}
          onClick={handleCreateNew}
          aria-label="Create new hosted agent"
          data-testid="create-hosted-agent-button"
        >
          <div className={styles.createNewIcon}>
            <Add16Regular aria-hidden="true" />
          </div>
          <Text className={styles.createNewText}>
            Create new hosted agent
          </Text>
        </button>

        {/* Agent list */}
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <Spinner size="tiny" />
          </div>
        ) : agents.length === 0 ? (
          <div className={styles.emptyState}>
            <PlugDisconnected16Regular aria-hidden="true" className={styles.emptyIcon} />
            <Text className={styles.emptyText}>No hosted agents found</Text>
          </div>
        ) : (
          <ul className={styles.agentList} role="list" aria-label="Available hosted agents">
            {agents.map((agent) => {
              const isSelected = selectedAgentName === agent.key;
              return (
                <li key={agent.key}>
                  <button
                    type="button"
                    className={mergeClasses(
                      styles.agentItem,
                      isSelected && styles.agentItemSelected,
                    )}
                    onClick={() => handleSelectAgent(agent.key)}
                    aria-pressed={isSelected}
                    data-testid={`hosted-agent-item-${agent.key}`}
                  >
                    {renderStatusGlyph(agent.status)}
                    <div className={styles.agentItemInfo}>
                      <Text className={styles.agentItemName}>{agent.displayName}</Text>
                      <Text className={styles.agentItemModel}>{agent.configuration.modelDeployment}</Text>
                    </div>
                    <Badge
                      size="small"
                      color={getStatusBadgeColor(agent.status)}
                      appearance="filled"
                    >
                      {STATUS_LABELS[agent.status] ?? STATUS_LABELS.unknown}
                    </Badge>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};
